import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, OperatorFunction, pipe, Subject } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';

import { mapRooms } from 'src/app/helpers/utils/map.utils';
import { getErrorKey } from 'src/app/helpers/utils/notifications.utils';
import { isRoomsWithDifferentCurrencies } from 'src/app/helpers/utils/rooms.utils';
import { hasAllSearchValues } from 'src/app/helpers/utils/search.utils';
import { createRoomSearchParams } from 'src/app/helpers/utils/http-params.utils';

import { ApplicationInsightsService } from 'src/app/services/application-insights/application-insights.service';
import { LoaderService } from 'src/app/services/loader/loader.service';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { RoomStoreService } from 'src/app/services/room/room-store.service';

import { RoomFilterCriteria } from 'src/app/modules/booking/components/filter-rooms/filter-rooms.models';
import { CentreLocation } from 'src/app/shared/models/centreLocation.model';
import { DefaultError } from 'src/app/shared/constants/errors.constants';
import { HttpStatus } from 'src/app/shared/constants/httpStatus.enum';
import { Room, RoomType } from 'src/app/shared/models/room.model';
import { RoomDetails } from 'src/app/shared/models/roomDetails.model';
import RoomSearchParameters from 'src/app/shared/models/roomSearchParams.model';
import { RoomSearchResponse } from 'src/app/shared/models/roomSearchResponse.model';

import { environment } from 'src/environments/environment';

export interface RoomsResult {
  rooms: Room[];
  type: RoomType;
}

export enum Tabs {
  ROOM_VIEW,
  MAP_VIEW
}

const PREFETCH_THRESHOLD = 60;
const AVAILABLE_BATCH_SIZE = 60;
const INITIAL_BATCH_SIZE = 20;

const searchApi = 'rooms/search/all-with-availability';
const allCentresApi = 'rooms/all-centres';

@Injectable({
  providedIn: 'root'
})
export class RoomService {
  public updateSearch = new Subject<void>();
  public updateParameters = new BehaviorSubject<Partial<RoomSearchParameters>>(null);
  public isMapVisible = new BehaviorSubject<boolean>(false);
  public defaultSearch = new BehaviorSubject<boolean>(true);
  public searchParams = new BehaviorSubject<Partial<RoomSearchParameters>>(null);
  public selectedRoom = new BehaviorSubject<Room>(null);
  public highlightedRoom = new BehaviorSubject<Room>(null);
  public seeAvailabilityTrigger = new Subject<void>();

  public isRoomsWithDifferentCurrencies = false;

  public updateRoomsDetails$: Observable<RoomDetails>;
  public singleRoom$: Observable<Room>;
  public selectedRoom$: Observable<Room>;
  public roomSearchParameters$: Observable<Partial<RoomSearchParameters>>;
  public seeAvailabilityTrigger$: Observable<void>;
  public defaultSearch$: Observable<boolean>;

  public roomsStream$: Observable<Room[]>;

  public scrollToRoom$: Observable<Room>;
  public updateSearch$: Observable<void>;

  public allCentres$: Observable<CentreLocation[]>;
  public lookupCache$: Observable<Room[]>;
  public pendingLookups$: Observable<Set<number>>;

  public activeTab: BehaviorSubject<Tabs> = new BehaviorSubject<Tabs>(Tabs.ROOM_VIEW);

  private readonly updateRoomsDetails = new Subject<RoomDetails>();
  private readonly singleRoom = new Subject<Room>();
  private readonly scrollToRoomSubject = new BehaviorSubject<Room | null>(null);

  private readonly allCentresSubject = new BehaviorSubject<CentreLocation[]>([]);
  private readonly lookupCacheSubject = new BehaviorSubject<Room[]>([]);
  private readonly pendingLookupsSubject = new BehaviorSubject<Set<number>>(new Set<number>());
  private allCentresLoaded = false;

  _proceedWithSelectedRoom = new Subject<void>();

  private currentType = null;
  private readonly prefetchCancellation$ = new Subject<void>();
  private readonly prefetchCompleted = new Subject<void>();
  public readonly prefetchCompleted$ = this.prefetchCompleted.asObservable();
  private isPrefetchInProgress = false;

  constructor(
    private readonly applicationInsightsService: ApplicationInsightsService,
    private readonly http: HttpClient,
    private readonly notificationsService: NotificationsService,
    private readonly loader: LoaderService,
    private readonly roomStore: RoomStoreService
  ) {
    this.updateRoomsDetails$ = this.updateRoomsDetails.asObservable();
    this.singleRoom$ = this.singleRoom.asObservable();
    this.selectedRoom$ = this.selectedRoom.asObservable();
    this.roomSearchParameters$ = this.searchParams.asObservable();
    this.seeAvailabilityTrigger$ = this.seeAvailabilityTrigger.asObservable();
    this.defaultSearch$ = this.defaultSearch.asObservable();
    this.scrollToRoom$ = this.scrollToRoomSubject.asObservable();
    this.updateSearch$ = this.updateSearch.asObservable();

    this.allCentres$ = this.allCentresSubject.asObservable();
    this.lookupCache$ = this.lookupCacheSubject.asObservable();
    this.pendingLookups$ = this.pendingLookupsSubject.asObservable();

    this.roomsStream$ = this.roomStore.rooms$;
  }

  scrollToRoom(room: Room): void {
    this.scrollToRoomSubject.next(room);
  }

  public proceedWithSelectedRoom(): void {
    this._proceedWithSelectedRoom.next();
  }

  // Apply filter against the in-memory buffer — no API call.
  public applyFrontendFilter(criteria: RoomFilterCriteria): void {
    this.roomStore.setFilterCriteria(criteria);
  }

  // Advance display by one page. Returns true if buffer served the page (no API call needed).
  public showNextPageFromBuffer(): boolean {
    return this.roomStore.showNextPage();
  }

  public hasMoreInBuffer(): boolean {
    return this.roomStore.hasMoreInBuffer();
  }

  public get isLoadingMore(): boolean {
    return this.isPrefetchInProgress;
  }

  public getRoomFilterCriteria(): RoomFilterCriteria {
    return this.roomStore.getFilterCriteria();
  }

  public setSelectedRoom(room: Room): void {
    this.selectedRoom.next(room);
    sessionStorage.setItem('selectedRoom', JSON.stringify(room));
  }

  public clearSelectedRoom(): void {
    this.selectedRoom.next(null);
    sessionStorage.removeItem('selectedRoom');
  }

  public setHighlightedRoom(room: Room): void {
    this.highlightedRoom.next(room);
  }

  public getRoomDetails(centreNumber: number): void {
    this.http
      .get(`${environment.apiUrl}rooms/${centreNumber}/details`)
      .pipe(
        catchError((err) => {
          this.notificationsService.pushNotification(getErrorKey(err, 'Cannot get room details'));
          return err;
        })
      )
      .subscribe((response: RoomDetails) => {
        this.updateRoomsDetails.next(response);
      });
  }

  public setSearchState(searchState: Partial<RoomSearchParameters>): void {
    this.searchParams.next(searchState);
    sessionStorage.setItem('searchState', JSON.stringify(searchState));
  }

  public getSearchState(): Partial<RoomSearchParameters> {
    let search = this.searchParams.value;

    if (!search) {
      search = JSON.parse(sessionStorage.getItem('searchState'));
      this.searchParams.next(search);
    }
    return search;
  }

  public modifySearchState(searchState: Partial<RoomSearchParameters>): void {
    const currentState = this.searchParams.value;
    const newState = { ...currentState, ...searchState };
    this.setSearchState(newState);
  }

  public getSearchMethod(params: Partial<RoomSearchParameters>): Observable<RoomsResult | RoomSearchResponse> {
    const isFullSearch = hasAllSearchValues(params);
    const hasCentre = !!params.centreNumber;

    const roomType = isFullSearch ? RoomType.AVAILABLE : RoomType.INITIAL;

    if (this.currentType !== roomType) {
      this.resetRooms();
      this.currentType = roomType;
      params.lastCentreNumber = null;
    }

    this.setIsDefaultSearch(!isFullSearch);

    if (hasCentre) {
      return this.searchOne(params).pipe(this.processSearchResult());
    }

    return this.searchAll(params);
  }

  public searchOne(parameters: Partial<RoomSearchParameters>): Observable<Room[]> {
    return this.fetchSingleRoom(parameters).pipe(
      map((room: Room | any): Room[] => {
        if (!room || room.message) {
          return [];
        }

        this.singleRoom.next(room);
        return [room];
      }),
      this.handleRoomSearchError()
    );
  }

  public loadAllCentres(): void {
    if (this.allCentresLoaded) return;
    this.allCentresLoaded = true;

    this.http
      .get<CentreLocation[]>(`${environment.apiUrl}${allCentresApi}`)
      .pipe(
        catchError((err): Observable<CentreLocation[]> => {
          this.allCentresLoaded = false;
          this.applicationInsightsService.logError(err);
          return of([]);
        })
      )
      .subscribe((centres: CentreLocation[]) => {
        this.allCentresSubject.next(centres ?? []);
      });
  }

  public lookupCentre(centreNumber: number): void {
    const cached = this.lookupCacheSubject.value;
    const cachedEntry = cached.find((r) => r.centreNumber === centreNumber);
    if (cachedEntry && !cachedEntry.isLoadingError) return;

    const pending = this.pendingLookupsSubject.value;
    if (pending.has(centreNumber)) return;

    if (cachedEntry?.isLoadingError) {
      this.lookupCacheSubject.next(cached.filter((r) => r.centreNumber !== centreNumber));
    }

    const nextPending = new Set(pending);
    nextPending.add(centreNumber);
    this.pendingLookupsSubject.next(nextPending);

    const search = this.getSearchState() ?? {};
    const parameters: Partial<RoomSearchParameters> = {
      ...search,
      centreNumber: String(centreNumber)
    };

    this.fetchSingleRoom(parameters)
      .pipe(
        map((response: Room | any): Room | null => {
          if (response && !response.message) {
            return response as Room;
          }
          return this.buildUnavailablePlaceholder(centreNumber);
        }),
        catchError((): Observable<Room | null> => of(this.buildErrorRoom(centreNumber)))
      )
      .subscribe((room) => {
        this.addToLookupCache(room);
        this.clearPendingLookup(centreNumber);
      });
  }

  private fetchSingleRoom(parameters: Partial<RoomSearchParameters>): Observable<Room | any> {
    return this.http.get<Room>(`${environment.apiUrl}rooms/${parameters.centreNumber}`, {
      params: createRoomSearchParams(parameters)
    });
  }

  private addToLookupCache(room: Room | null): void {
    if (!room) return;
    const current = this.lookupCacheSubject.value;
    if (current.some((r) => r.centreNumber === room.centreNumber)) return;
    const mapped = mapRooms([room])[0];
    this.lookupCacheSubject.next([...current, mapped]);
  }

  private clearPendingLookup(centreNumber: number): void {
    const pending = this.pendingLookupsSubject.value;
    if (!pending.has(centreNumber)) return;
    const next = new Set(pending);
    next.delete(centreNumber);
    this.pendingLookupsSubject.next(next);
  }

  private buildUnavailablePlaceholder(centreNumber: number): Room {
    const lite = this.allCentresSubject.value.find((c) => c.number === centreNumber);
    return {
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        cityName: '',
        countryCodeIso2: '',
        countryName: '',
        coordinates: lite
          ? { latitude: lite.latitude, longitude: lite.longitude }
          : { latitude: 0, longitude: 0 }
      },
      centreName: lite?.name ?? '',
      centreNumber,
      enquiryCount: 0,
      isHighDemand: false,
      isUnavailable: true,
      isWebBookable: true,
      labels: null,
      mainPhotoUrl: '',
      price: null
    } as Room;
  }

  private buildErrorRoom(centreNumber: number): Room {
    const lite = this.allCentresSubject.value.find((c) => c.number === centreNumber);
    return {
      address: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        cityName: '',
        countryCodeIso2: '',
        countryName: '',
        coordinates: lite
          ? { latitude: lite.latitude, longitude: lite.longitude }
          : { latitude: 0, longitude: 0 }
      },
      centreName: lite?.name ?? '',
      centreNumber,
      enquiryCount: 0,
      isHighDemand: false,
      isLoadingError: true,
      isWebBookable: true,
      labels: null,
      mainPhotoUrl: '',
      price: null
    } as Room;
  }

  private search(
    incomingParameters: Partial<RoomSearchParameters>,
    centreCount: number = AVAILABLE_BATCH_SIZE
  ): Observable<Room[]> {
    const lastFoundRoom: Room = this.roomStore.getLastFoundRoom();
    if (lastFoundRoom) {
      incomingParameters.lastCentreNumber = lastFoundRoom.centreNumber;
    }

    const params = createRoomSearchParams(incomingParameters as RoomSearchParameters, centreCount);

    return this.http.get(`${environment.apiUrl}${searchApi}`, { params }).pipe(
      this.handleRoomSearchError()
    );
  }

  public resetRooms(): void {
    this.prefetchCancellation$.next();
    this.isPrefetchInProgress = false;
    this.roomStore.reset();
    this.lookupCacheSubject.next([]);
    this.pendingLookupsSubject.next(new Set<number>());
    const search = this.getSearchState();
    this.setSearchState({ ...search, lastCentreNumber: null });
  }

  private setIsDefaultSearch(value: boolean): void {
    this.defaultSearch.next(value);
  }

  private searchAll(incomingParameters: RoomSearchParameters): Observable<Room[]> {
    this.prefetchCancellation$.next();
    this.isPrefetchInProgress = false;
    this.loader.start();
    const parameters = Object.assign({}, incomingParameters);
    this.clearSelectedRoom();

    if (!parameters.location && !parameters.coordinatesLongitude && !parameters.coordinatesLatitude) {
      this.applicationInsightsService.logError(new Error(JSON.stringify(parameters)));
    }

    const source$: Observable<Room[]> = this.search(parameters, INITIAL_BATCH_SIZE);
    return source$.pipe(
      this.processSearchResult(),
      tap(() => this.prefetchNextBatch(parameters))
    );
  }

  private prefetchNextBatch(baseParameters: RoomSearchParameters): void {
    if (!this.roomStore.getLastFoundRoom() || this.isPrefetchInProgress) return;
    this.isPrefetchInProgress = true;
    this.backgroundFetch(baseParameters)
      .pipe(
        finalize(() => {
          this.isPrefetchInProgress = false;
          this.prefetchCompleted.next();
        })
      )
      .subscribe();
  }

  public shouldPrefetchMore(): boolean {
    return !this.isPrefetchInProgress && this.roomStore.getRemainingCount() <= PREFETCH_THRESHOLD;
  }

  public isFinalBatch(rooms: Room[]): boolean {
    return rooms.length < INITIAL_BATCH_SIZE;
  }

  public loadMoreInBackground(): Observable<boolean> {
    if (this.isPrefetchInProgress || !this.roomStore.getLastFoundRoom()) return of(false);
    this.isPrefetchInProgress = true;
    const params = { ...this.searchParams.value } as RoomSearchParameters;
    return this.backgroundFetch(params).pipe(
      map((r) => !this.isFinalBatch(r ?? [])),
      finalize(() => {
        this.isPrefetchInProgress = false;
        this.prefetchCompleted.next();
      })
    );
  }

  private backgroundFetch(parameters: RoomSearchParameters): Observable<Room[]> {
    return this.search(parameters).pipe(
      takeUntil(this.prefetchCancellation$),
      map((response: Room[]) => {
        if (response?.length) {
          const rooms = mapRooms(response);
          this.roomStore.addRooms(rooms);
          return rooms;
        }
        return response;
      }),
      catchError((): Observable<Room[]> => of([]))
    );
  }

  private processSearchResult(): OperatorFunction<Room[], Room[]> {
    return pipe(
      map((response: Room[]) => {
        const rooms = mapRooms(response);

        this.isRoomsWithDifferentCurrencies = isRoomsWithDifferentCurrencies(rooms);
        this.roomStore.addRooms(rooms);
        this.loader.stop();

        return rooms;
      }),
      catchError((): Observable<Room[]> => {
        this.roomStore.reset();
        this.loader.stop();
        return of([]);
      })
    );
  }

  private readonly handleRoomSearchError = (): OperatorFunction<Room[], Room[]> =>
    catchError((err): Observable<Room[]> => {
      if (err?.status === HttpStatus.CODE500) {
        this.notificationsService.pushNotification(getErrorKey(err, DefaultError));
      }
      return of([]);
    });
}
